import React, { useState, useEffect, useRef } from "react";
import apiAxios from "../../../services/apiAxios";
import toast from "react-hot-toast";

const INITIAL_RECORDS = {
    notes: "",
    observations: [],
    treatments: [],
    visitCount: 1,
    complaints: [],
    diagnosis: [],
    testsPrescribed: [],
    measurements: [],
    advice: [],
    diagrams: [],
    images: [],
};

const nextVisitOptions = [
    "5 Days",
    "10 Days",
    "15 Days",
    "1 Week",
    "2 Weeks",
    "3 Weeks",
    "1 Month",
    "2 Months",
    "3 Months",
    "6 Months",
    "1 Year",
];

const calculateNextVisitDate = (option) => {
    const now = new Date();
    switch (option) {
        case "5 Days": now.setDate(now.getDate() + 5); break;
        case "10 Days": now.setDate(now.getDate() + 10); break;
        case "15 Days": now.setDate(now.getDate() + 15); break;
        case "1 Week": now.setDate(now.getDate() + 7); break;
        case "2 Weeks": now.setDate(now.getDate() + 14); break;
        case "3 Weeks": now.setDate(now.getDate() + 21); break;
        case "1 Month": now.setMonth(now.getMonth() + 1); break;
        case "2 Months": now.setMonth(now.getMonth() + 2); break;
        case "3 Months": now.setMonth(now.getMonth() + 3); break;
        case "6 Months": now.setMonth(now.getMonth() + 6); break;
        case "1 Year": now.setFullYear(now.getFullYear() + 1); break;
        default: return null;
    }
    return now;
};

const formatDateShort = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const PatientHealthRecords = ({
    readOnly,
    data,
    onChange,
    diagnosticsNotes, setDiagnosticsNotes, // legacy
    nextVisit, setNextVisit,
    nextVisitDate, setNextVisitDate,
    referredBy, setReferredBy,
    referredTo, setReferredTo,
    DID,
    lastSavedAt,
}) => {
    const [activeTab, setActiveTab] = useState(null);
    const [dropdownAlign, setDropdownAlign] = useState("left-0");
    const [gridCols, setGridCols] = useState(5);
    const [globalParams, setGlobalParams] = useState({
        complaint: [],
        diagnosis: [],
        test: [],
        measurement: [],
        observation: [],
        advice: [],
        diagram: [],
    });
    const [mostUsedParams, setMostUsedParams] = useState({});
    const [unsavedImagesUrls, setUnsavedImagesUrls] = useState([]); // Tracking for cleanup
    const [isUploading, setIsUploading] = useState(false);
    const [pendingImage, setPendingImage] = useState(null); // { file, previewUrl }
    const dropdownRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [valueInput, setValueInput] = useState("");

    // Clear unsaved list when prescription is saved
    useEffect(() => {
        if (lastSavedAt) {
            setUnsavedImagesUrls([]);
        }
    }, [lastSavedAt]);

    useEffect(() => {
        const fetchMostUsed = async () => {
            if (!activeTab) return;
            // Map tab key to type key if needed
            const typeKeyMap = {
                complaints: 'complaint',
                diagnosis: 'diagnosis',
                tests: 'test',
                measurements: 'measurement',
                advice: 'advice',
                observations: 'observation',
                treatments: 'treatment',
                diagrams: 'diagram'
            };
            const type = typeKeyMap[activeTab];
            if (!type) return;

            try {
                const res = await apiAxios.get(`/api/global-parameters?type=${type}`);
                if (res.data.success) {
                    const mostUsed = res.data.mostUsed || [];
                    setMostUsedParams(prev => ({
                        ...prev,
                        [activeTab]: mostUsed
                    }));
                }
            } catch (err) {
                console.error("Most Used Fetch Error:", err);
            }
        };

        if (activeTab) fetchMostUsed();
    }, [activeTab]);

    // Diagram Modal State
    const [isDiagramModalOpen, setIsDiagramModalOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null); // Full-screen image preview
    const [diagTitle, setDiagTitle] = useState("");
    const [diagSubtitle, setDiagSubtitle] = useState("");
    const [diagImageUrl, setDiagImageUrl] = useState("");
    const containerRef = useRef(null);

    // Dynamically compute balanced grid columns based on container width
    useEffect(() => {
        const TOTAL_ITEMS = 14; // 13 tabs + 1 visit count stepper
        const MIN_BTN_WIDTH = 120; // minimum button width in px
        const GAP = 8; // gap-2 = 8px

        const compute = () => {
            if (!containerRef.current) return;
            const containerWidth = containerRef.current.offsetWidth;
            // Max columns that physically fit
            const maxCols = Math.max(1, Math.floor((containerWidth + GAP) / (MIN_BTN_WIDTH + GAP)));
            // How many rows at that max column count
            const numRows = Math.ceil(TOTAL_ITEMS / maxCols);
            // Balanced columns = spread items as evenly as possible across those rows
            const balancedCols = Math.ceil(TOTAL_ITEMS / numRows);
            setGridCols(balancedCols);
        };

        compute();
        window.addEventListener("resize", compute);
        return () => window.removeEventListener("resize", compute);
    }, []);

    const handlePatientImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Create a local preview URL
        const previewUrl = URL.createObjectURL(file);
        setPendingImage({ file, previewUrl });
    };

    const confirmPatientImageUpload = async () => {
        if (!pendingImage) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append("image", pendingImage.file);

        try {
            const res = await apiAxios.post("/api/global-parameters/upload-patient-image", formData);
            if (res.data.success) {
                const newUrl = res.data.imageUrl;
                const newImages = [...(data.images || []), { imageUrl: newUrl, note: "" }];
                handleUpdate("images", newImages);
                setUnsavedImagesUrls(prev => [...prev, newUrl]); // Track for session cleanup

                // Cleanup local preview
                URL.revokeObjectURL(pendingImage.previewUrl);
                setPendingImage(null);
                toast.success("Image added to record!");
            }
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Failed to upload image");
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemovePatientImage = (idx) => {
        const newList = [...data.images];
        const removed = newList.splice(idx, 1)[0];
        handleUpdate("images", newList);

        // Always attempt to delete from S3 when explicitly removed from the list
        apiAxios.delete(`/api/global-parameters/delete-image?url=${encodeURIComponent(removed.imageUrl)}`).catch(e => console.warn("Cleanup failed", e));
    };

    const handleCancel = () => {
        // Just close the dropdown and clear pending (un-uploaded) images
        if (pendingImage) {
            URL.revokeObjectURL(pendingImage.previewUrl);
            setPendingImage(null);
        }
        setActiveTab(null);
        setSearchTerm("");
        setValueInput("");
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                handleCancel();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingImage]);

    // Fetch global parameters on mount
    useEffect(() => {
        const fetchParams = async () => {
            try {
                const res = await apiAxios.get("/api/global-parameters");
                if (res.data?.success) {
                    setGlobalParams({
                        complaint: res.data.parameters.complaint || [],
                        diagnosis: res.data.parameters.diagnosis || [],
                        test: res.data.parameters.test || [],
                        measurement: res.data.parameters.measurement || [],
                        advice: res.data.parameters.advice || [],
                        observation: res.data.parameters.observation || [],
                        treatment: res.data.parameters.treatment || [],
                        diagram: res.data.parameters.diagram || [],
                    });
                }
            } catch (err) {
                console.error("Failed to load global parameters", err);
            }
        };
        fetchParams();
    }, []);

    // Track unsaved images for session cleanup using a ref to avoid closure issues with effects
    const unsavedImagesRef = useRef([]);

    useEffect(() => {
        unsavedImagesRef.current = unsavedImagesUrls;
    }, [unsavedImagesUrls]);

    // This effect clears the unsaved list if a prescription was successfully generated
    useEffect(() => {
        if (lastSavedAt) {
            setUnsavedImagesUrls([]);
        }
    }, [lastSavedAt]);

    useEffect(() => {
        if (data?.images?.length === 0 && unsavedImagesUrls.length > 0) {
            setUnsavedImagesUrls([]);
        }
    }, [data.images, unsavedImagesUrls.length]);

    const handleDiagramUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append("image", file);

        try {
            const res = await apiAxios.post("/api/global-parameters/upload-diagram", formData);
            if (res.data.success) {
                setDiagImageUrl(res.data.imageUrl);
                toast.success("Image uploaded successfully!");
            }
        } catch (error) {
            console.error("Upload error:", error);
            const errMsg = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to upload image.";
            toast.error(errMsg);
        } finally {
            setIsUploading(false);
        }
    };

    const handleAddDiagram = async () => {
        if (!diagTitle.trim()) {
            toast.error("Please enter a title");
            return;
        }

        try {
            const payload = {
                type: "diagram",
                name: diagTitle.trim(),
                subtitle: diagSubtitle.trim(),
                imageUrl: diagImageUrl,
                status: "pending",
            };

            const res = await apiAxios.post("/api/global-parameters", payload);
            if (res.data.success) {
                const newDiag = res.data.parameter;
                // Add to globalParams locally for immediate use
                setGlobalParams(prev => ({
                    ...prev,
                    diagram: [...(prev.diagram || []), newDiag]
                }));

                // Auto-select it
                const currentDiagrams = [...(data.diagrams || [])];
                handleUpdate("diagrams", [...currentDiagrams, {
                    parameter: newDiag.name,
                    value: "",
                    title: newDiag.name,
                    subtitle: newDiag.subtitle,
                    imageUrl: newDiag.imageUrl
                }]);

                // Reset and close
                setIsDiagramModalOpen(false);
                setDiagTitle("");
                setDiagSubtitle("");
                setDiagImageUrl("");
                toast.success("Diagram added successfully!");
            }
        } catch (error) {
            console.error("Add diagram error:", error);
            toast.error("Failed to add diagram.");
        }
    };

    const handleUpdate = (key, val) => {
        if (readOnly) return;
        onChange({ ...data, [key]: val });
    };

    const handleAddMultiSelect = (typeKey, recordKey, needsValue) => {
        if (!searchTerm.trim()) return;

        const newItem = needsValue
            ? { parameter: searchTerm.trim(), value: valueInput.trim() }
            : searchTerm.trim();

        const currentList = data[recordKey] || [];

        const isDuplicate = needsValue
            ? currentList.some(i => i.parameter.toLowerCase() === searchTerm.trim().toLowerCase())
            : currentList.some(i => i.toLowerCase() === searchTerm.trim().toLowerCase());

        if (isDuplicate) {
            toast.error("Parameter already added.");
            return;
        }

        handleUpdate(recordKey, [...currentList, newItem]);

        // Optimistically add to globalParams list so it shows in the checkbox options instantly
        const exactMatchGlobal = globalParams[typeKey]?.find(opt => opt.name.toLowerCase() === searchTerm.trim().toLowerCase());
        if (!exactMatchGlobal) {
            const newName = searchTerm.trim();
            setGlobalParams(prev => ({
                ...prev,
                [typeKey]: [{ id: Date.now(), name: newName }, ...(prev[typeKey] || [])]
            }));

            // Persist immediately so it survives page reloads
            apiAxios.post("/api/global-parameters", {
                type: typeKey,
                name: newName,
                did: DID
            })
                .catch(err => console.error("Failed to save global parameter immediately", err));
        }

        setSearchTerm("");
        setValueInput("");
    };

    const handleRemoveMultiSelect = (recordKey, index) => {
        if (readOnly) return;
        const currentList = [...(data[recordKey] || [])];
        currentList.splice(index, 1);
        handleUpdate(recordKey, currentList);
    };

    const renderItemRow = (opt, isSelected, currItem, recordKey, needsValue, selectedList) => (
        <div key={opt.id} className="flex items-center gap-3 px-3 py-2 hover:bg-primary-50 group transition-colors">
            <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                    if (e.target.checked) {
                        if (needsValue) {
                            handleUpdate(recordKey, [...selectedList, { parameter: opt.name, value: "" }]);
                        } else {
                            handleUpdate(recordKey, [...selectedList, opt.name]);
                        }
                    } else {
                        const idx = needsValue
                            ? selectedList.findIndex(i => i.parameter === opt.name)
                            : selectedList.findIndex(i => i === opt.name);
                        handleRemoveMultiSelect(recordKey, idx);
                    }
                }}
                className="w-4 h-4 text-primary-600 border-primary-300 rounded outline-none cursor-pointer"
            />
            <span
                className={`text-[13px] cursor-pointer flex-1 ${isSelected ? "text-primary-800 font-semibold" : "text-secondry-700 group-hover:text-primary-700"}`}
                onClick={() => {
                    if (!isSelected) {
                        if (needsValue) {
                            handleUpdate(recordKey, [...selectedList, { parameter: opt.name, value: "" }]);
                        } else {
                            handleUpdate(recordKey, [...selectedList, opt.name]);
                        }
                    } else {
                        const idx = needsValue
                            ? selectedList.findIndex(i => i.parameter === opt.name)
                            : selectedList.findIndex(i => i === opt.name);
                        handleRemoveMultiSelect(recordKey, idx);
                    }
                }}
            >
                {opt.name}
            </span>

            {needsValue && isSelected && (
                <input
                    type="text"
                    placeholder="Details..."
                    value={currItem?.value || ""}
                    onChange={(e) => {
                        const newList = [...selectedList];
                        const idx = selectedList.findIndex(i => i.parameter === opt.name);
                        newList[idx] = { ...newList[idx], value: e.target.value };
                        handleUpdate(recordKey, newList);
                    }}
                    className="w-[140px] h-[30px] px-2 border border-primary-200 rounded-[6px] text-[12px] outline-none focus:border-primary-500 shadow-sm"
                />
            )}
        </div>
    );

    const renderDiagramRow = (opt, isSelected, currItem, selectedList) => (
        <div key={opt.id} className="flex p-3 border-b border-white last:border-b-0 hover:bg-white transition-colors group">
            <div className="flex-shrink-0 pt-1">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                        if (e.target.checked) {
                            handleUpdate("diagrams", [...selectedList, {
                                parameter: opt.name,
                                value: "",
                                title: opt.name,
                                subtitle: opt.subtitle,
                                imageUrl: opt.imageUrl
                            }]);
                        } else {
                            const idx = selectedList.findIndex(i => i.parameter === opt.name);
                            handleRemoveMultiSelect("diagrams", idx);
                        }
                    }}
                    className="w-4 h-4 text-primary-600 border-primary-300 rounded cursor-pointer"
                />
            </div>

            <div className="ml-3 flex-1 flex gap-3 cursor-pointer" onClick={() => {
                if (!isSelected) {
                    handleUpdate("diagrams", [...selectedList, {
                        parameter: opt.name,
                        value: "",
                        title: opt.name,
                        subtitle: opt.subtitle,
                        imageUrl: opt.imageUrl
                    }]);
                } else {
                    const idx = selectedList.findIndex(i => i.parameter === opt.name);
                    handleRemoveMultiSelect("diagrams", idx);
                }
            }}>
                {opt.imageUrl && (
                    <div className="w-14 h-14 rounded-md border border-primary-100 overflow-hidden bg-white flex-shrink-0 group-hover:border-primary-300 transition-colors">
                        <img src={opt.imageUrl} className="w-full h-full object-contain" alt={opt.name} />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className={`text-[13px] font-semibold truncate ${isSelected ? "text-primary-800" : "text-primary-900 group-hover:text-primary-700"}`}>{opt.name}</div>
                    {opt.subtitle && <div className="text-[11px] text-primary-500 line-clamp-2 mt-0.5">{opt.subtitle}</div>}

                    {isSelected && (
                        <div className="mt-2 text-[12px] flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <span className="text-primary-400">Note:</span>
                            <input
                                type="text"
                                placeholder="Add brief note..."
                                value={currItem?.value || ""}
                                onChange={(e) => {
                                    const newList = [...selectedList];
                                    const idx = selectedList.findIndex(i => i.parameter === opt.name);
                                    newList[idx] = { ...newList[idx], value: e.target.value };
                                    handleUpdate("diagrams", newList);
                                }}
                                className="flex-1 h-[26px] px-2 border border-primary-200 rounded-[4px] outline-none focus:border-primary-500 shadow-sm"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderDiagramSelectContent = () => {
        const selectedList = data.diagrams || [];
        const options = globalParams.diagram || [];
        const filteredOptions = options.filter(opt =>
            opt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (opt.subtitle && opt.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        return (
            <div className={`absolute top-full ${dropdownAlign} mt-2 w-[320px] sm:w-[550px] bg-white border border-primary-200 shadow-xl rounded-[12px] z-50 p-4`} ref={dropdownRef}>
                <div className="flex justify-between items-center mb-4">
                    <h4 className="text-[14px] font-bold text-primary-800">Diagrams</h4>
                    <button
                        type="button"
                        onClick={() => setIsDiagramModalOpen(true)}
                        className="text-[12px] font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 px-3 py-1.5 rounded-full transition-colors border border-primary-100"
                    >
                        + Add New Diagram
                    </button>
                </div>

                {/* Selected Diagrams */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {selectedList.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-primary-100 px-3 py-1.5 rounded-[8px] text-[12px] text-primary-800">
                            {item.imageUrl && <img src={item.imageUrl} className="w-4 h-4 rounded-sm object-cover" alt="" />}
                            <span>{item.parameter}{item.value ? `: ${item.value}` : ""}</span>
                            <button type="button" onClick={() => handleRemoveMultiSelect("diagrams", idx)} className="text-primary-500 hover:text-danger-500 ml-1">
                                &times;
                            </button>
                        </div>
                    ))}
                    {selectedList.length === 0 && <span className="text-[12px] text-secondry-400 italic">No diagrams selected.</span>}
                </div>

                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        placeholder="Search diagrams..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="flex-1 h-[38px] px-3 border border-primary-200 rounded-[8px] text-[13px] outline-none focus:border-primary-500"
                    />
                </div>

                <div className="max-h-[350px] overflow-y-auto border border-primary-50 rounded-[10px] bg-primary-25/30 divide-y divide-white/50">
                    {/* Most Used Diagrams */}
                    {!searchTerm && mostUsedParams.diagrams?.length > 0 && (
                        <div className="bg-primary-50/30">
                            <div className="px-3 py-2 text-[10px] font-bold text-primary-500 uppercase tracking-wider flex items-center gap-1.5">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" /></svg>
                                Most Used
                            </div>
                            {mostUsedParams.diagrams.map(opt => {
                                const isSelected = selectedList.some(i => i.parameter === opt.name);
                                const currItem = selectedList.find(i => i.parameter === opt.name);
                                return renderDiagramRow(opt, isSelected, currItem, selectedList);
                            })}
                        </div>
                    )}

                    {/* All/Filtered Diagrams */}
                    <div>
                        {!searchTerm && mostUsedParams.diagrams?.length > 0 && (
                            <div className="px-3 py-2 text-[10px] font-bold text-secondry-400 uppercase tracking-wider">
                                All Suggestions
                            </div>
                        )}
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(opt => {
                                const isSelected = selectedList.some(i => i.parameter === opt.name);
                                const currItem = selectedList.find(i => i.parameter === opt.name);
                                return renderDiagramRow(opt, isSelected, currItem, selectedList);
                            })
                        ) : (
                            <div className="px-3 py-6 text-[13px] text-secondry-400 italic text-center">
                                No diagrams found. Click "+ Add New" to upload one.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderImageUploadContent = () => {
        const selectedList = data.images || [];

        return (
            <div className={`absolute top-full ${dropdownAlign} mt-2 w-[320px] sm:w-[480px] bg-white border border-primary-200 shadow-xl rounded-[12px] z-50 p-4`} ref={dropdownRef}>
                <div className="flex justify-between items-center mb-4">
                    <h4 className="text-[14px] font-bold text-primary-800">Patient Images</h4>
                    <div className="relative">
                        <button
                            type="button"
                            className={`text-[12px] font-bold px-4 py-1.5 rounded-full border transition-all bg-primary-50 text-primary-600 border-primary-200 hover:bg-primary-100`}
                        >
                            Select Image
                        </button>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handlePatientImageUpload}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                    </div>
                </div>

                {/* Pending Upload Preview */}
                {pendingImage && (
                    <div className="mb-4 p-3 border-2 border-dashed border-primary-200 rounded-[12px] bg-primary-25/50 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex items-center gap-4">
                            <div className="w-20 h-20 rounded-[10px] border border-primary-100 bg-white p-1 overflow-hidden flex-shrink-0 shadow-sm">
                                <img src={pendingImage.previewUrl} className="w-full h-full object-contain" alt="Pending" />
                            </div>
                            <div className="flex-1">
                                <p className="text-[12px] font-bold text-primary-800 mb-1">New Image Selected</p>
                                <p className="text-[10px] text-gray-500 mb-3 truncate max-w-[200px]">{pendingImage.file.name}</p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={confirmPatientImageUpload}
                                        disabled={isUploading}
                                        className="flex-1 h-[32px] bg-emerald-600 text-white rounded-[6px] text-[11px] font-bold hover:bg-emerald-700 transition-colors shadow-md shadow-emerald-100 disabled:opacity-50"
                                    >
                                        {isUploading ? "Uploading..." : "Add to Record"}
                                    </button>
                                    <button
                                        onClick={() => { URL.revokeObjectURL(pendingImage.previewUrl); setPendingImage(null); }}
                                        className="px-3 h-[32px] bg-white border border-gray-200 text-gray-400 rounded-[6px] text-[11px] font-bold hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-1">
                    {selectedList.map((item, idx) => (
                        <div key={idx} className="p-3 border border-primary-100 rounded-[12px] bg-gray-50/30 flex gap-3 group hover:border-primary-200 transition-colors">
                            <div className="w-16 h-16 rounded-[8px] border border-primary-100 bg-white p-1 overflow-hidden flex-shrink-0 shadow-sm">
                                <img src={item.imageUrl} className="w-full h-full object-contain" alt="" />
                            </div>
                            <div className="flex-1">
                                <textarea
                                    placeholder="Add a clinical note..."
                                    value={item.note || ""}
                                    onChange={(e) => {
                                        const newList = [...selectedList];
                                        newList[idx] = { ...newList[idx], note: e.target.value };
                                        handleUpdate("images", newList);
                                    }}
                                    className="w-full h-[40px] px-2 py-1 text-[12px] border border-primary-100 rounded-[6px] outline-none focus:border-primary-400 resize-none bg-white transition-all"
                                />
                                <div className="flex justify-end mt-1">
                                    <button
                                        type="button"
                                        onClick={() => handleRemovePatientImage(idx)}
                                        className="text-[10px] font-bold text-danger-500 hover:underline uppercase tracking-wider opacity-60 group-hover:opacity-100 transition-opacity"
                                    >
                                        Delete Image
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {selectedList.length === 0 && !pendingImage && (
                        <div className="py-8 text-center">
                            <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E62121" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                            </div>
                            <p className="text-[13px] text-gray-500 italic">No clinical images added.</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderMultiSelectContent = (typeKey, recordKey, title, needsValue = false) => {
        const selectedList = data[recordKey] || [];
        const options = globalParams[typeKey] || [];
        const filteredOptions = options.filter(opt =>
            opt.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        const exactMatch = options.find(opt => opt.name.toLowerCase() === searchTerm.trim().toLowerCase());

        return (
            <div className={`absolute top-full ${dropdownAlign} mt-2 w-[320px] sm:w-[500px] bg-white border border-primary-200 shadow-xl rounded-[8px] z-50 p-4`} ref={dropdownRef}>
                <h4 className="text-[14px] font-semibold text-primary-700 mb-3">{title}</h4>

                {/* Selected Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {selectedList.map((item, idx) => {
                        const label = needsValue ? `${item.parameter}${item.value ? `: ${item.value}` : ""}` : item;
                        return (
                            <div key={idx} className="flex items-center gap-2 bg-primary-100 px-3 py-1.5 rounded-[8px] text-[13px] text-primary-800">
                                <span>{label}</span>
                                {!readOnly && (
                                    <button onClick={() => handleRemoveMultiSelect(recordKey, idx)} className="text-primary-500 hover:text-danger-500 focus:outline-none">
                                        &times;
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {selectedList.length === 0 && <span className="text-[12px] text-secondry-400">No {title.toLowerCase()} selected yet.</span>}
                </div>

                {!readOnly && (
                    <>
                        {/* Search and Add */}
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                placeholder={`Search ${title.toLowerCase()}...`}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="flex-1 h-[38px] px-3 border border-primary-200 rounded-[8px] text-[13px] outline-none focus:border-primary-500"
                            />
                            {searchTerm.trim() && !exactMatch && (
                                <>
                                    {needsValue && (
                                        <input
                                            type="text"
                                            placeholder="Value..."
                                            value={valueInput}
                                            onChange={e => setValueInput(e.target.value)}
                                            className="w-[100px] h-[38px] px-3 border border-primary-200 rounded-[8px] text-[13px] outline-none focus:border-primary-500"
                                        />
                                    )}
                                    <button
                                        onClick={() => handleAddMultiSelect(typeKey, recordKey, needsValue)}
                                        className="h-[38px] px-4 bg-primary-600 text-white rounded-[8px] text-[13px] font-medium hover:bg-primary-700"
                                    >
                                        Add New
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Dropdown Options (Always visible with checkboxes) */}
                        <div className="max-h-[280px] overflow-y-auto border border-primary-100 rounded-[8px] bg-white divide-y divide-primary-50">
                            {/* Most Used Section */}
                            {!searchTerm && mostUsedParams[activeTab]?.length > 0 && (
                                <div className="bg-primary-25/50 border-b border-primary-100">
                                    <div className="px-3 py-1.5 text-[10px] font-bold text-primary-500 uppercase tracking-wider flex items-center gap-1.5">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" /></svg>
                                        Most Used
                                    </div>
                                    <div className="pb-1">
                                        {mostUsedParams[activeTab].map(opt => {
                                            const isSelected = needsValue ? selectedList.some(i => i.parameter === opt.name) : selectedList.includes(opt.name);
                                            const currItem = needsValue ? selectedList.find(i => i.parameter === opt.name) : null;
                                            return renderItemRow(opt, isSelected, currItem, recordKey, needsValue, selectedList);
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* All/Filtered Options */}
                            <div className="py-1">
                                {!searchTerm && mostUsedParams[activeTab]?.length > 0 && (
                                    <div className="px-3 py-1.5 text-[10px] font-bold text-secondry-400 uppercase tracking-wider">
                                        All Suggestions
                                    </div>
                                )}
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map(opt => {
                                        const isSelected = needsValue ? selectedList.some(i => i.parameter === opt.name) : selectedList.includes(opt.name);
                                        const currItem = needsValue ? selectedList.find(i => i.parameter === opt.name) : null;
                                        return renderItemRow(opt, isSelected, currItem, recordKey, needsValue, selectedList);
                                    })
                                ) : (
                                    <div className="px-3 py-4 text-[13px] text-secondry-400 italic text-center">
                                        No parameters matched. Click "Add New" to save it.
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderTextContent = (recordKey, title, placeholder) => (
        <div className={`absolute top-full ${dropdownAlign} mt-2 w-[320px] sm:w-[500px] bg-white border border-primary-200 shadow-xl rounded-[8px] z-50 p-4`} ref={dropdownRef}>
            <h4 className="text-[14px] font-semibold text-primary-700 mb-3">{title}</h4>
            <textarea
                value={data[recordKey] || ""}
                onChange={(e) => handleUpdate(recordKey, e.target.value)}
                placeholder={placeholder}
                readOnly={readOnly}
                rows={4}
                className="w-full p-3 border border-primary-200 rounded-[8px] text-[13px] outline-none focus:border-primary-500 resize-none"
            />
        </div>
    );

    const toggleTab = (tab, e) => {
        if (activeTab === tab) {
            setActiveTab(null);
        } else {
            setActiveTab(tab);
            setSearchTerm("");
            setValueInput("");
            if (e && e.currentTarget) {
                const rect = e.currentTarget.getBoundingClientRect();
                const dropdownWidth = window.innerWidth >= 640 ? 500 : 320;
                // If there's not enough space on the right, align right-0 so it expands leftward
                if (window.innerWidth - rect.left < dropdownWidth) {
                    setDropdownAlign("right-0");
                } else {
                    setDropdownAlign("left-0");
                }
            }
        }
    };

    const renderNextVisitContent = () => (
        <div className={`absolute top-full ${dropdownAlign} mt-2 w-[320px] sm:w-[350px] bg-white border border-primary-200 shadow-xl rounded-[8px] z-50 p-4`} ref={dropdownRef}>
            <h4 className="text-[14px] font-semibold text-primary-700 mb-3">Next Visit</h4>
            <div className="flex flex-col gap-1 max-h-[180px] overflow-y-auto mb-3 border border-primary-50 rounded-[8px]">
                {nextVisitOptions.map(opt => (
                    <div
                        key={opt}
                        onClick={() => {
                            const calcDate = calculateNextVisitDate(opt);
                            setNextVisit(opt);
                            setNextVisitDate(calcDate);
                            setActiveTab(null);
                        }}
                        className="px-3 py-2 hover:bg-primary-50 text-[13px] cursor-pointer text-secondry-700 border-b border-primary-50 last:border-b-0 flex justify-between items-center"
                    >
                        <span>{opt}</span>
                        <span className="text-[11px] text-primary-400">{formatDateShort(calculateNextVisitDate(opt))}</span>
                    </div>
                ))}
            </div>
            <div className="pt-2 border-t border-primary-100">
                <input
                    type="text"
                    value={nextVisit || ""}
                    onChange={(e) => setNextVisit(e.target.value)}
                    placeholder="Custom next visit..."
                    className="w-full h-[38px] px-3 border border-primary-200 rounded-[8px] text-[13px] outline-none focus:border-primary-500"
                />
            </div>
        </div>
    );

    const renderReferredByContent = () => (
        <div className={`absolute top-full ${dropdownAlign} mt-2 w-[320px] sm:w-[350px] bg-white border border-primary-200 shadow-xl rounded-[8px] z-50 p-4`} ref={dropdownRef}>
            <h4 className="text-[14px] font-semibold text-primary-700 mb-3">Referred By</h4>
            <input
                type="text"
                value={referredBy || ""}
                onChange={(e) => setReferredBy(e.target.value)}
                placeholder="Enter doctor or clinic name..."
                className="w-full h-[40px] px-3 border border-primary-200 rounded-[8px] text-[13px] outline-none focus:border-primary-500"
                autoFocus
            />
        </div>
    );

    const renderReferredToContent = () => (
        <div className={`absolute top-full ${dropdownAlign} mt-2 w-[320px] sm:w-[350px] bg-white border border-primary-200 shadow-xl rounded-[8px] z-50 p-4`} ref={dropdownRef}>
            <h4 className="text-[14px] font-semibold text-primary-700 mb-3">Referred To</h4>
            <input
                type="text"
                value={referredTo || ""}
                onChange={(e) => setReferredTo(e.target.value)}
                placeholder="Enter doctor or clinic name referring to..."
                className="w-full h-[40px] px-3 border border-primary-200 rounded-[8px] text-[13px] outline-none focus:border-primary-500"
                autoFocus
            />
        </div>
    );

    const tabs = [
        { id: "complaints", label: "Complaints", render: () => renderMultiSelectContent("complaint", "complaints", "Patient Complaints", true) },
        { id: "diagnosis", label: "Diagnosis", render: () => renderMultiSelectContent("diagnosis", "diagnosis", "Diagnosis", true) },
        { id: "measurements", label: "Measurements", render: () => renderMultiSelectContent("measurement", "measurements", "Measurements", true) },
        { id: "testsPrescribed", label: "Tests Prescribed", render: () => renderMultiSelectContent("test", "testsPrescribed", "Tests Prescribed", true) },
        { id: "advice", label: "Advice", render: () => renderMultiSelectContent("advice", "advice", "Doctor's Advice", true) },
        { id: "observations", label: "Observations", render: () => renderMultiSelectContent("observation", "observations", "Clinical Observations", true) },
        { id: "treatments", label: "Treatment", render: () => renderMultiSelectContent("treatment", "treatments", "Treatment Plan", true) },
        { id: "diagrams", label: "Diagrams", render: () => renderDiagramSelectContent() },
        { id: "images", label: "Images", render: () => renderImageUploadContent() },
        { id: "notes", label: "Notes", render: () => renderTextContent("notes", "General Notes", "Enter any general notes here...") },
        { id: "nextVisit", label: "Next Visit", render: renderNextVisitContent, value: nextVisit },
        { id: "referredBy", label: "Referred By", render: renderReferredByContent, value: referredBy },
        { id: "referredTo", label: "Referred To", render: renderReferredToContent, value: referredTo },
    ];

    const getAddedRecords = () => {
        const records = [];

        if (data.complaints?.length > 0) {
            const mapped = data.complaints.map(m => typeof m === 'string' ? m : `${m.parameter}${m.value ? `: ${m.value}` : ""}`).join(", ");
            records.push({ label: "Complaints", value: mapped });
        }
        if (data.diagnosis?.length > 0) {
            const mapped = data.diagnosis.map(m => typeof m === 'string' ? m : `${m.parameter}${m.value ? `: ${m.value}` : ""}`).join(", ");
            records.push({ label: "Diagnosis", value: mapped });
        }
        if (data.measurements?.length > 0) {
            const mapped = data.measurements.map(m => typeof m === 'string' ? m : `${m.parameter}${m.value ? `: ${m.value}` : ""}`).join(", ");
            records.push({ label: "Measurements", value: mapped });
        }
        if (data.testsPrescribed?.length > 0) {
            const mapped = data.testsPrescribed.map(m => typeof m === 'string' ? m : `${m.parameter}${m.value ? `: ${m.value}` : ""}`).join(", ");
            records.push({ label: "Tests Prescribed", value: mapped });
        }
        if (data.advice?.length > 0) {
            const mapped = data.advice.map(m => `${m.parameter}${m.value ? `: ${m.value}` : ""}`).join(", ");
            records.push({ label: "Advice", value: mapped });
        }
        if (data.observations?.length > 0) {
            const mapped = data.observations.map(m => `${m.parameter}${m.value ? `: ${m.value}` : ""}`).join(", ");
            records.push({ label: "Observations", value: mapped });
        }
        if (data.treatments?.length > 0) {
            const mapped = data.treatments.map(m => `${m.parameter}${m.value ? `: ${m.value}` : ""}`).join(", ");
            records.push({ label: "Treatment", value: mapped });
        }
        if (data.notes?.trim()) {
            records.push({ label: "Notes", value: data.notes });
        }
        if (nextVisit?.trim()) {
            records.push({ label: "Next Visit", value: nextVisit });
        }
        if (referredBy?.trim()) {
            records.push({ label: "Referred By", value: referredBy });
        }
        if (referredTo?.trim()) {
            records.push({ label: "Referred To", value: referredTo });
        }

        if (data.diagrams?.length > 0) {
            records.push({
                label: "Diagrams",
                value: data.diagrams,
                type: 'diagrams',
                icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                    </svg>
                ),
                color: "bg-indigo-50 text-indigo-700 border-indigo-100"
            });
        }

        if (data.images?.length > 0) {
            records.push({
                label: "Patient Images",
                value: data.images,
                type: 'images',
                icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                    </svg>
                ),
                color: "bg-emerald-50 text-emerald-700 border-emerald-100"
            });
        }

        return records;
    };

    const CategoryIcon = ({ label }) => {
        const icons = {
            "Complaints": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
            ),
            "Diagnosis": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
            ),
            "Measurements": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M2 12h20M7 2v5M17 2v5M7 17v5M17 17v5" />
                </svg>
            ),
            "Tests Prescribed": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                </svg>
            ),
            "Advice": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
            ),
            "Observations": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
            ),
            "Patient Images": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                </svg>
            ),
            "Notes": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
            ),
            "Next Visit": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            ),
            "Referred By": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <polyline points="17 11 19 13 23 9" />
                </svg>
            ),
            "Referred To": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <line x1="20" y1="8" x2="20" y2="14" />
                    <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
            ),
            "Treatment": (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M2 12h20" />
                </svg>
            ),
        };
        return icons[label] || null;
    };

    const CategoryColors = (label) => {
        const colors = {
            "Complaints": "bg-danger-50 text-danger-700 border-danger-100",
            "Diagnosis": "bg-primary-50 text-primary-700 border-primary-100",
            "Measurements": "bg-amber-50 text-amber-700 border-amber-100",
            "Tests Prescribed": "bg-indigo-50 text-indigo-700 border-indigo-100",
            "Advice": "bg-success-50 text-success-700 border-success-100",
            "Observations": "bg-purple-50 text-purple-700 border-purple-100",
            "Notes": "bg-gray-50 text-gray-700 border-gray-100",
            "Next Visit": "bg-blue-50 text-blue-700 border-blue-100",
            "Treatment": "bg-indigo-50 text-indigo-700 border-indigo-100",
        };
        return colors[label] || "bg-primary-50 text-primary-700 border-primary-100";
    };

    const addedRecords = getAddedRecords();

    return (
        <div className="p-4 rounded-[8px] mb-2 bg-primary-50">
            {/*<label className="block text-primary-800 font-semibold text-[14px] mb-3">Patient Health Records</label>*/}

            <div
                ref={containerRef}
                className="grid gap-2"
                style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
            >
                {/* Visit Count Stepper (FIRST) */}
                <div className="relative w-full">
                    <div className="flex items-center bg-white border border-primary-100 rounded-[8px] h-[37px] px-1 w-full overflow-hidden transition-all focus-within:border-primary-500">
                        <button
                            type="button"
                            onClick={() => handleUpdate("visitCount", Math.max(1, (parseInt(data.visitCount) || 1) - 1))}
                            disabled={readOnly || (parseInt(data.visitCount) || 1) <= 1}
                            className="w-[26px] h-[26px] flex-shrink-0 flex items-center justify-center text-[16px] font-bold text-primary-600 hover:bg-primary-50 rounded-[4px] disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        >
                            −
                        </button>
                        <div className="flex-1 flex items-center justify-center">
                            <span className="text-[12px] sm:text-[13px] font-medium text-primary-700 whitespace-nowrap">
                                Visit&nbsp;<span className="font-semibold">#{data.visitCount || 1}</span>
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleUpdate("visitCount", (parseInt(data.visitCount) || 1) + 1)}
                            disabled={readOnly}
                            className="w-[26px] h-[26px] flex-shrink-0 flex items-center justify-center text-[16px] font-bold text-primary-600 hover:bg-primary-50 rounded-[4px] disabled:opacity-20 transition-colors"
                        >
                            +
                        </button>
                    </div>
                </div>

                {tabs.map(tab => {
                    const valueAtId = data[tab.id];
                    const hasContent = (
                        (Array.isArray(valueAtId) && valueAtId.length > 0) ||
                        (typeof valueAtId === 'string' && valueAtId.trim().length > 0) ||
                        (typeof tab.value === 'string' && tab.value.trim().length > 0)
                    );

                    return (
                        <div key={tab.id} className="relative w-full">
                            <button
                                type="button"
                                onClick={(e) => toggleTab(tab.id, e)}
                                className={`w-full h-[37px] px-2 border rounded-[8px] text-[13px] font-medium transition-all duration-200 flex items-center justify-center 
                                    ${activeTab === tab.id
                                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                        : hasContent
                                            ? 'bg-success-100 border-success-600 text-success-600 font-bold'
                                            : 'bg-white text-primary-700 border-primary-100 hover:bg-primary-100'
                                    }`}
                            >
                                {tab.label}
                            </button>
                            {activeTab === tab.id && tab.render()}
                        </div>
                    );
                })}
            </div>

            {addedRecords.length > 0 && (
                <div className="mt-4 pt-4 border-t border-primary-100">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center text-white shadow-sm">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-[13px] font-semibold text-primary-900 tracking-tight">Clinical Summary</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {addedRecords.map((record, index) => {
                            if (record.type === 'diagrams' || record.type === 'images') return null;

                            return (
                                <div key={index} className="flex flex-col gap-1.5 p-2 rounded-[10px] bg-white border border-primary-50/50">
                                    <div className="flex items-center gap-1.5">
                                        <div className={`flex items-center justify-center w-5 h-5 rounded-md ${CategoryColors(record.label).split(' ')[0]} ${CategoryColors(record.label).split(' ')[1]}`}>
                                            <CategoryIcon label={record.label} />
                                        </div>
                                        <span className="text-[10px] font-regular uppercase tracking-widest text-gray-400">{record.label}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 pl-0.5">
                                        {record.label === 'Notes' || record.label === 'Next Visit' || record.label.includes('Referred') ? (
                                            <span className="text-[13px] text-gray-700 font-medium leading-normal break-words">{record.value}</span>
                                        ) : (
                                            record.value.split(', ').map((pill, pIdx) => (
                                                <span key={pIdx} className="inline-flex items-center px-2 py-0.5 rounded-[6px] bg-gray-50/50 text-gray-600 text-[12px] font-medium border border-gray-100/30">
                                                    {pill}
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Compact Diagrams */}
                        {data.diagrams?.length > 0 && (
                            <div className="col-span-full mt-1 flex flex-col gap-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-primary-400 pl-1">Visual Aids</span>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                                    {data.diagrams.map((diag, dIdx) => (
                                        <div key={dIdx} className="group relative flex flex-col bg-white border border-indigo-50/50 rounded-[12px] overflow-hidden hover:border-indigo-200 transition-all p-2">
                                            {diag.imageUrl && (
                                                <div
                                                    className="w-full aspect-square bg-gray-50/50 rounded-[8px] flex items-center justify-center p-2 cursor-zoom-in"
                                                    onClick={() => setPreviewImage({ url: diag.imageUrl, title: diag.parameter, subtitle: diag.subtitle, value: diag.value })}
                                                >
                                                    <img src={diag.imageUrl} className="max-w-full max-h-full object-contain drop-shadow-sm transition-transform group-hover:scale-105" alt="" />
                                                </div>
                                            )}
                                            <div className="mt-2 min-w-0">
                                                <div className="text-[11px] font-bold text-indigo-900 truncate">{diag.parameter}</div>
                                                {diag.subtitle && <div className="text-[9px] text-indigo-500 font-medium truncate leading-none mb-1">{diag.subtitle}</div>}
                                                {diag.value && <div className="text-[10px] text-indigo-400 mt-0.5 truncate italic" title={diag.value}>{diag.value}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Patient Images */}
                        {data.images?.length > 0 && (
                            <div className="col-span-full mt-3 flex flex-col gap-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400 pl-1">Clinical Photos</span>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                                    {data.images.map((img, iIdx) => (
                                        <div key={iIdx} className="group relative flex flex-col bg-white border border-emerald-50/50 rounded-[12px] overflow-hidden hover:border-emerald-200 transition-all p-2">
                                            {img.imageUrl && (
                                                <div
                                                    className="w-full aspect-square bg-gray-50/50 rounded-[8px] flex items-center justify-center p-2 cursor-zoom-in"
                                                    onClick={() => setPreviewImage({ url: img.imageUrl, title: "Clinical Photo", value: img.note })}
                                                >
                                                    <img src={img.imageUrl} className="max-w-full max-h-full object-contain drop-shadow-sm transition-transform group-hover:scale-105" alt="" />
                                                </div>
                                            )}
                                            {img.note && (
                                                <div className="mt-2 min-w-0">
                                                    <div className="text-[10px] text-emerald-600 font-medium leading-tight line-clamp-2 italic" title={img.note}>
                                                        "{img.note}"
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Add New Diagram Modal */}
            {isDiagramModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-[450px] rounded-[16px] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-5 border-b border-primary-50 flex justify-between items-center">
                            <h3 className="text-[16px] font-bold text-primary-800">Add New Diagram</h3>
                            <button type="button" onClick={() => setIsDiagramModalOpen(false)} className="text-secondry-400 hover:text-danger-500 transition-colors">&times;</button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-[12px] font-bold text-primary-600 mb-1 uppercase tracking-wider">Diagram Title *</label>
                                <input
                                    type="text"
                                    value={diagTitle}
                                    onChange={(e) => setDiagTitle(e.target.value)}
                                    placeholder="e.g. Lower Back Pain Area"
                                    className="w-full h-[42px] px-3 border border-primary-100 rounded-[10px] text-[13px] outline-none focus:border-primary-500 transition-all font-medium"
                                />
                            </div>

                            <div>
                                <label className="block text-[12px] font-bold text-primary-600 mb-1 uppercase tracking-wider">Subtitle / Description</label>
                                <textarea
                                    value={diagSubtitle}
                                    onChange={(e) => setDiagSubtitle(e.target.value)}
                                    placeholder="Brief description of the diagram..."
                                    rows={2}
                                    className="w-full p-3 border border-primary-100 rounded-[10px] text-[13px] outline-none focus:border-primary-500 transition-all resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-[12px] font-bold text-primary-600 mb-2 uppercase tracking-wider">Diagram Image</label>
                                {!diagImageUrl ? (
                                    <div className="relative h-[120px] border-2 border-dashed border-primary-100 rounded-[12px] flex flex-col items-center justify-center gap-2 bg-primary-25/30 hover:bg-primary-50 hover:border-primary-300 transition-all group overflow-hidden">
                                        {isUploading ? (
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                                                <span className="text-[12px] font-medium text-primary-600">Uploading to S3...</span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-primary-500">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                                                </div>
                                                <span className="text-[12px] font-semibold text-primary-500">Pick an image</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleDiagramUpload}
                                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                                />
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="relative h-[140px] rounded-[12px] border border-primary-100 bg-white p-2 group overflow-hidden">
                                        <img src={diagImageUrl} className="w-full h-full object-contain" alt="Preview" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                                            <button
                                                type="button"
                                                onClick={() => setDiagImageUrl("")}
                                                className="bg-primary-100 text-secondary-600 px-3 py-1.5 rounded-full text-[12px] font-bold shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-all"
                                            >
                                                Change Image
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <p className="mt-2 text-[10px] text-primary-400 font-medium text-center italic">
                                    Recommended: Square Aspect Ratio (1:1) • Formats: .png, .jpg, .jpeg
                                </p>
                            </div>
                        </div>

                        <div className="p-5 bg-primary-25/50 border-t border-primary-50 flex gap-3">
                            <button
                                type="button"
                                onClick={() => setIsDiagramModalOpen(false)}
                                className="flex-1 h-[42px] border border-primary-200 text-primary-600 rounded-[10px] text-[13px] font-bold hover:bg-white transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddDiagram}
                                disabled={isUploading}
                                className="flex-[2] h-[42px] bg-primary-600 text-white rounded-[10px] text-[13px] font-bold shadow-lg shadow-primary-200 hover:bg-primary-700 active:scale-[0.98] transition-all disabled:opacity-50"
                            >
                                {isUploading ? "Uploading..." : "Add to Prescription"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Full-screen Image Preview Lightbox */}
            {previewImage && (
                <div className="hm-overlay" onClick={() => setPreviewImage(null)} style={{ zIndex: 9999 }}>
                    <div className="hm-overlay-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "min(90vw, 800px)", background: "#1a1a1a", padding: "12px", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.7)" }}>
                        <button className="hm-overlay-close" onClick={() => setPreviewImage(null)} style={{ top: -45, right: 0 }}>✕</button>
                        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: "16px" }}>
                            <img
                                src={previewImage.url || previewImage}
                                alt={previewImage.title || "Full Preview"}
                                style={{ width: "100%", height: "auto", display: "block" }}
                            />
                            {(previewImage.title || previewImage.subtitle || previewImage.value) && (
                                <div style={{ color: "#fff", textAlign: "center", display: "flex", flexDirection: "column", gap: 2, background: "rgba(255,255,255,0.03)", padding: "16px 12px" }}>
                                    {previewImage.title && <div style={{ fontSize: 18, fontWeight: 700 }}>{previewImage.title}</div>}
                                    {previewImage.subtitle && (
                                        <div style={{ fontSize: 14, opacity: 0.8 }}>{previewImage.subtitle}</div>
                                    )}
                                    {previewImage.value && (
                                        <div style={{ fontSize: 13, background: "rgba(255,255,255,0.05)", padding: "8px 16px", borderRadius: 8, marginTop: 8, border: "1px solid rgba(255,255,255,0.08)" }}>
                                            <span style={{ opacity: 0.6, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 2 }}>Note</span>
                                            {previewImage.value}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { INITIAL_RECORDS };
export default PatientHealthRecords;
